import { emptyStream, type ChatStream, type PendingMessage } from './core'

type StreamChange = (current: ChatStream) => ChatStream

/**
 * Sends the server hasn't confirmed, kept for as long as the tab lives — as iOS keeps
 * them on its Chat tab — so one that fails after I leave the Chat is there, with its
 * retry, when I come back. While a Chat is shown its stream holds them; the outbox holds
 * them in between. Never stored.
 */
export interface Outbox {
	/** The server's ids for a send's photos once uploaded, by client id, so a retry doesn't upload them again. */
	readonly uploadedPhotoIds: Map<string, string[]>
	/**
	 * Hands my sends to a shown stream, which holds them from here on: `update` changes
	 * it, `current` reads it. Returns the detach that takes them back when it closes; one
	 * closing after another stream was attached leaves that one be.
	 */
	attach: (update: (change: StreamChange) => void, current: () => ChatStream) => () => void
	/** Changes my sends wherever they are now: in the shown stream, or else in the outbox. */
	changeSends: (change: StreamChange) => void
}

export const newOutbox = (): Outbox => {
	let pending: readonly PendingMessage[] = []
	let attached: ((change: StreamChange) => void) | null = null
	return {
		uploadedPhotoIds: new Map(),
		attach: (update, current) => {
			update(stream => ({ ...stream, pending }))
			attached = update
			return () => {
				if (attached !== update) return
				pending = current().pending
				attached = null
			}
		},
		changeSends: change => {
			if (attached) attached(change)
			else pending = change({ ...emptyStream(), pending }).pending
		},
	}
}

const outboxes = new Map<string, Outbox>()

/**
 * Each trip's Chat has one outbox per person for the life of the tab: signing out doesn't
 * reload the page, and the next person in mustn't see, or send, mine.
 */
export const outboxFor = (userId: number | undefined, tripId: string): Outbox => {
	const key = `${userId ?? ''}:${tripId}`
	let outbox = outboxes.get(key)
	if (!outbox) {
		outbox = newOutbox()
		outboxes.set(key, outbox)
	}
	return outbox
}
